import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AnalyticsFilters from './components/AnalyticsFilters';
import PerformanceKPIStrip from './components/PerformanceKPIStrip';
import EquityCurveChart from './components/EquityCurveChart';
import InstrumentPerformanceTable from './components/InstrumentPerformanceTable';
import TradingHeatmap from './components/TradingHeatmap';

const PortfolioAnalytics = () => {
  const [filters, setFilters] = useState({
    dateRange: '1M',
    selectedAccounts: ['main'],
    baseCurrency: 'USD'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Auto-refresh data every 5 minutes
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setLastUpdated(new Date());
      // In a real app, this would trigger data refresh
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      setIsLoading(false);
      setLastUpdated(new Date());
    }, 1000);
  };

  const handleManualRefresh = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setLastUpdated(new Date());
    }, 1000);
  };

  const handleExportData = (format) => {
    // In a real app, this would trigger data export
    console.log(`Exporting data in ${format} format`);
  };

  const formatLastUpdated = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })?.format(date);
  };

  return (
    <>
      <Helmet>
        <title>Portfolio Analytics - Zonex Trader</title>
        <meta name="description" content="Comprehensive trading performance analysis with interactive data visualization and trend analysis for forex and financial instruments." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pb-20 lg:pb-0">
          <div className="p-6">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  Portfolio Analytics
                </h1>
                <p className="text-muted-foreground">
                  Comprehensive trading performance analysis and insights
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
                {/* Auto-refresh toggle */}
                <div className="flex items-center space-x-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e?.target?.checked)}
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                    />
                    <span className="text-sm text-muted-foreground">Auto-refresh</span>
                  </label>
                </div>

                {/* Manual refresh */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManualRefresh}
                  disabled={isLoading}
                  iconName={isLoading ? "Loader2" : "RefreshCw"}
                  iconPosition="left"
                  className={isLoading ? "animate-spin" : ""}
                >
                  {isLoading ? 'Refreshing...' : 'Refresh'}
                </Button>

                {/* Export dropdown */}
                <div className="relative group">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                  >
                    Export
                  </Button>
                  
                  <div className="absolute top-full right-0 mt-1 w-40 bg-popover border border-border rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto z-50">
                    <div className="p-1">
                      <button
                        onClick={() => handleExportData('pdf')}
                        className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors duration-200"
                      >
                        <Icon name="FileText" size={16} className="inline mr-2" />
                        Export to PDF
                      </button>
                      <button
                        onClick={() => handleExportData('excel')}
                        className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors duration-200"
                      >
                        <Icon name="FileSpreadsheet" size={16} className="inline mr-2" />
                        Export to Excel
                      </button>
                      <button
                        onClick={() => handleExportData('csv')}
                        className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors duration-200"
                      >
                        <Icon name="Download" size={16} className="inline mr-2" />
                        Export to CSV
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Last Updated Info */}
            <div className="flex items-center justify-between mb-6 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Clock" size={16} />
                <span>Last updated: {formatLastUpdated(lastUpdated)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${isLoading ? 'bg-amber-500 animate-pulse' : 'bg-green-500'}`} />
                <span className="text-sm text-muted-foreground">
                  {isLoading ? 'Updating...' : 'Live data'}
                </span>
              </div>
            </div>

            {/* Filters */}
            <AnalyticsFilters onFiltersChange={handleFiltersChange} />

            {/* Loading Overlay */}
            {isLoading && (
              <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
                <div className="bg-card border border-border rounded-lg p-6 flex items-center space-x-3">
                  <Icon name="Loader2" size={20} className="animate-spin text-primary" />
                  <span className="text-foreground">Updating analytics...</span>
                </div>
              </div>
            )}

            {/* KPI Strip */}
            <PerformanceKPIStrip 
              dateRange={filters?.dateRange}
              selectedAccounts={filters?.selectedAccounts}
            />

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 mb-8">
              {/* Equity Curve Chart - 8 columns on desktop */}
              <div className="xl:col-span-8">
                <EquityCurveChart 
                  dateRange={filters?.dateRange}
                  selectedAccounts={filters?.selectedAccounts}
                />
              </div>

              {/* Instrument Performance Table - 4 columns on desktop */}
              <div className="xl:col-span-4">
                <InstrumentPerformanceTable 
                  dateRange={filters?.dateRange}
                  selectedAccounts={filters?.selectedAccounts}
                />
              </div>
            </div>

            {/* Trading Heatmap - Full width */}
            <TradingHeatmap 
              dateRange={filters?.dateRange}
              selectedAccounts={filters?.selectedAccounts}
            />

            {/* Additional Analytics Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
              {/* Risk Metrics */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="Shield" size={20} className="text-primary" />
                  <h3 className="text-lg font-semibold text-foreground">Risk Metrics</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Value at Risk (95%)</span>
                    <span className="text-sm font-medium text-foreground">$2,847</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Maximum Exposure</span>
                    <span className="text-sm font-medium text-foreground">$45,600</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Risk/Reward Ratio</span>
                    <span className="text-sm font-medium text-green-600">1:2.4</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Correlation Risk</span>
                    <span className="text-sm font-medium text-amber-600">Medium</span>
                  </div>
                </div>
              </div>

              {/* Performance Summary */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="TrendingUp" size={20} className="text-primary" />
                  <h3 className="text-lg font-semibold text-foreground">Performance Summary</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Best Month</span>
                    <span className="text-sm font-medium text-green-600">+$8,947 (Sep)</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Worst Month</span>
                    <span className="text-sm font-medium text-red-600">-$2,134 (Feb)</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Consecutive Wins</span>
                    <span className="text-sm font-medium text-foreground">12 trades</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Profit Factor</span>
                    <span className="text-sm font-medium text-green-600">1.84</span>
                  </div>
                </div>
              </div>

              {/* Market Exposure */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="PieChart" size={20} className="text-primary" />
                  <h3 className="text-lg font-semibold text-foreground">Market Exposure</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Forex Majors</span>
                    <span className="text-sm font-medium text-foreground">68%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Forex Crosses</span>
                    <span className="text-sm font-medium text-foreground">18%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Commodities</span>
                    <span className="text-sm font-medium text-foreground">10%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Indices</span>
                    <span className="text-sm font-medium text-foreground">4%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default PortfolioAnalytics;